import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ChefHat, Coffee, Ticket, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Section, Container, Eyebrow, Stat, ArrowLink, Swash } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { Img, SIZES } from "@/components/shared/Img";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { Seo } from "@/components/shared/Seo";
import { fetchContent, fetchSite } from "@/lib/api";
import { paragraphs } from "@/lib/format";

/*
 * Velforeininga – the volunteers who keep the house running. Text comes from the «Velforeininga» content block
 * (editable in admin); the photographs are the house's own: the kitchen in full swing, the summer tent, the bar.
 */

const PHOTOS = {
  setting: "/api/images/b556fdd6-c2e8-43d8-af79-878aeedcd9d4",
  tent: "/api/images/2895fc43-924b-4892-b762-964d873cf6b2",
  table: "/api/images/ab8cbc9e-727c-434e-b2d0-f4afeff9c9fb",
};

/** The big volunteer efforts of the year – named in the content text; described here in one line each. */
const EFFORTS = [
  { num: "01", title: "Opningshelg", text: "Sesongen opnar med ei heil helg der huset er fullt av folk – på scena, i kafeen og på kjøkkenet." },
  { num: "02", title: "Street Food", text: "Mat, langbord og liv på plassen utanfor huset. Mange hender, mange kjøkken." },
  { num: "03", title: "Festivalveka", text: "Det største løftet i året: konsertar og arrangement gjennom ei heil veke, alt bore fram på dugnad." },
];

/** Ways in – straight from the text: waffles, stage, tickets, kitchen. */
const WAYS = [
  { icon: Coffee, title: "Steikje vaflar og servere", text: "Kafé og bar under arrangement – kaffi, vaflar og ein prat over disken." },
  { icon: Wrench, title: "Rigge scene og rom", text: "Bere, kople, lyssetje og rydde. Før dørene opnar og etter at siste gjest har gått." },
  { icon: Ticket, title: "Selje og skanne billettar", text: "Ta imot folk i døra, skanne billettar og vise veg til plassane." },
  { icon: ChefHat, title: "Halde orden på kjøkkenet", text: "Lage mat til mange, halde kjøkkenet i gang og sjå til at alt er på plass." },
];

export default function Velforeininga() {
  const { data: content, isLoading } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const vel = content?.velforeininga;
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const paras = paragraphs(vel?.body);
  const joinHref = `mailto:${email}?subject=${encodeURIComponent("Eg vil bidra på dugnad")}`;

  return (
    <div data-testid="vel-page">
      <Seo title="Velforeininga" description="Velforeininga er motoren i drifta av Halsnøy Kulturhus. Frivillige frå heile øya legg ned dugnadstimar på opningshelg, Street Food og Festivalveka. Bli med!" image={PHOTOS.setting} imageAlt="Dekking av langbord før fest i Stovene" path="/velforeininga" />

      <PageHeader
        eyebrow="Velforeininga"
        title={<Swash text="Dugnaden som held *huset* i gang" />}
        intro={paras[0] || "Velforeininga er motoren i drifta av kulturhuset. Frivillige frå heile øya legg ned dugnadstimar på arrangementa som gir huset inntekter."}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" data-testid="vel-join-button"><a href={joinHref}>Eg vil bidra <ArrowUpRight className="h-4 w-4" /></a></Button>
          <Button asChild variant="outline" size="lg" data-testid="vel-contact-link"><Link to="/kontakt">Kontakt oss</Link></Button>
        </div>
      </PageHeader>

      {/* The volunteers at work – one wide photograph, two smaller beneath */}
      <Container>
        <FadeUp>
          <figure data-testid="vel-hero-photo">
            <div className="img-hover relative overflow-hidden rounded-[12px] bg-secondary">
              <Img src={PHOTOS.setting} alt="Dekking av langbord før fest i Stovene" width={1600} sizes={SIZES.full} priority className="aspect-[3/2] w-full object-cover [object-position:50%_35%] sm:aspect-[2/1]" />
              <PhotoNumberTag src={PHOTOS.setting} interactive className="absolute right-2.5 top-2.5" testId="vel-hero-photo-number" />
            </div>
            <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 text-[13px] text-muted-foreground">
              <span>Dekking før fest i Stovene – mykje av dugnaden skjer før gjestene kjem.</span>
              <span className="t-mono-sm">Frå huset sitt eige album</span>
            </figcaption>
          </figure>
        </FadeUp>
      </Container>

      {/* WHO WE ARE */}
      <Section id="kven" eyebrow="Kven vi er" className="pt-14 sm:pt-16 lg:pt-20">
        {isLoading ? <Skeleton className="h-48 rounded-[12px]" /> : (
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <h2 className="t-h2 text-balance">{vel?.title && vel.title !== "Velforeininga" ? vel.title : <>Frivillige frå <em className="t-swash">heile øya</em></>}</h2>
              <div className="prose-nn mt-8 max-w-2xl text-pretty">
                {(paras.length > 1 ? paras.slice(1) : paras).map((p, i) => <p key={i} className={i === 0 ? "font-display text-[clamp(22px,2vw,30px)] font-medium leading-[1.25] tracking-[-0.02em]" : "text-[17px] leading-relaxed text-foreground/85"}>{p}</p>)}
              </div>
            </div>
            <div className="cellgrid grid-cols-2 self-end lg:col-span-4 lg:col-start-9 lg:grid-cols-1" data-testid="vel-stats">
              <Stat value="100 %" label="dugnad – ingen faste tilsette" />
              <Stat value="3" label="store dugnadsløft i året" />
            </div>
          </div>
        )}
      </Section>

      {/* THE BIG EFFORTS – three columns on hairlines */}
      <Section id="dugnadsloft" eyebrow="Dei store løfta" title={<>Arrangementa som <em className="t-swash">ber huset</em></>} subtitle="Dugnadsarrangementa er ei av dei viktigaste inntektskjeldene til kulturhuset. Overskotet går uavkorta til drift og vedlikehald." tight>
        <ol className="grid gap-x-8 gap-y-10 border-t border-border pt-10 md:grid-cols-3" data-testid="vel-efforts">
          {EFFORTS.map((e, i) => (
            <li key={e.title}>
              <FadeUp delay={i * 0.07}>
                <p className="t-mono flex items-center gap-2.5 text-muted-foreground"><span className="tabular">{e.num}</span><span className="h-px w-5 bg-current" aria-hidden="true" /></p>
                <h3 className="t-h3 mt-4">{e.title}</h3>
                <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-muted-foreground text-pretty">{e.text}</p>
              </FadeUp>
            </li>
          ))}
        </ol>
        <div className="mt-12 grid gap-3 sm:grid-cols-12">
          <figure className="img-hover relative overflow-hidden rounded-[12px] bg-secondary sm:col-span-7"><Img src={PHOTOS.tent} alt="Sommarkonsert i teltet utanfor kulturhuset" width={1200} sizes={SIZES.twoThirds} className="aspect-[3/2] h-full w-full object-cover" /><PhotoNumberTag src={PHOTOS.tent} interactive className="absolute right-2.5 top-2.5" testId="vel-photo-tent-number" /></figure>
          <figure className="img-hover relative overflow-hidden rounded-[12px] bg-secondary sm:col-span-5"><Img src={PHOTOS.table} alt="Langbord dekt til fest i Stovene" width={800} sizes={SIZES.third} className="aspect-[3/2] h-full w-full object-cover sm:aspect-auto" /><PhotoNumberTag src={PHOTOS.table} interactive className="absolute right-2.5 top-2.5" testId="vel-photo-table-number" /></figure>
        </div>
      </Section>

      {/* WAYS TO HELP */}
      <Section id="bidra" eyebrow="Slik kan du bidra" title={<>Alle kan noko. <em className="t-swash">Alt</em> trengst.</>} subtitle="Du treng ikkje kunne alt – ein kveld i blant er meir enn nok. Vel det du likar best, så finn vi ei vakt som passar.">
        <ul className="cellgrid sm:grid-cols-2 lg:grid-cols-4" data-testid="vel-ways">
          {WAYS.map(({ icon: Icon, title, text }, i) => (
            <li key={title} className="flex flex-col">
              <FadeUp delay={i * 0.06}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-foreground"><Icon className="h-5 w-5" /></span>
                <h3 className="t-h3 mt-5">{title}</h3>
                <p className="mt-2.5 text-[15px] leading-relaxed text-muted-foreground text-pretty">{text}</p>
              </FadeUp>
            </li>
          ))}
        </ul>
        <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[15px] text-muted-foreground">Send oss nokre ord om kva du har lyst til å gjere – så tek vi kontakt.</p>
          <ArrowLink href={joinHref} up data-testid="vel-join-link">Eg vil bidra</ArrowLink>
        </div>
      </Section>

      {/* CTA band */}
      <Section dark id="bli-med" eyebrow="Bli med">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-8">
          <div className="lg:col-span-8">
            <h2 className="t-h2 max-w-3xl text-balance text-paper">Eit hus utan tilsette er eit hus som treng <em className="t-swash">deg</em>.</h2>
            <p className="t-lead mt-6 max-w-2xl text-paper/75 text-pretty">Kvar dugnadstime held dørene opne, lyset på og prisane nede for alle som brukar huset. Ta kontakt – vi finn noko som passar for deg.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild variant="light" size="lg" data-testid="vel-cta-join"><a href={joinHref}>Eg vil bidra <ArrowUpRight className="h-4 w-4" /></a></Button>
              <Button asChild variant="lightOutline" size="lg"><Link to="/om-oss#styret">Sjå styret</Link></Button>
            </div>
          </div>
          <div className="lg:col-span-4">
            <Eyebrow light>Kontakt</Eyebrow>
            <a href={`mailto:${email}`} className="mt-3 block break-all text-[17px] text-paper u-hover" data-testid="vel-email">{email}</a>
            <p className="mt-2 text-[15px] text-paper/60">Skriv «dugnad» i emnefeltet, så hamnar det rett.</p>
          </div>
        </div>
      </Section>
    </div>
  );
}
